import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  ConsentTemplate,
  ConsentTemplateFilters,
  CreateConsentTemplateInput,
  UpdateConsentTemplateInput,
} from './types';

const all = ['consent-templates'] as const;

/**
 * GET /consent-templates?status: every revision (at most 200), by key then
 * newest first. Templates are clinic-wide (no branch scope).
 */
export function useConsentTemplates(filters: ConsentTemplateFilters) {
  return useQuery({
    queryKey: [...all, 'list', filters.status],
    queryFn: async ({ signal }) =>
      (
        await api.get<ConsentTemplate[]>('/consent-templates', {
          signal,
          query: { status: filters.status || undefined },
        })
      ).data,
  });
}

/** GET /consent-templates?key: the history of one key (every revision). */
export function useConsentTemplateHistory(key: string, enabled = true) {
  return useQuery({
    enabled,
    queryKey: [...all, 'key', key],
    queryFn: async ({ signal }) =>
      (
        await api.get<ConsentTemplate[]>('/consent-templates', {
          signal,
          query: { key },
        })
      ).data,
  });
}

function useTemplateMutation<I>(send: (input: I) => Promise<ConsentTemplate>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: send,
    onSettled: () => qc.invalidateQueries({ queryKey: all }),
  });
}

/** POST /consent-templates: a new key (revision 1) or the next revision of a key. */
export const useCreateConsentTemplate = () =>
  useTemplateMutation<CreateConsentTemplateInput>(
    async (body) =>
      (await api.post<ConsentTemplate>('/consent-templates', body)).data,
  );

/** PATCH /consent-templates/{id} {version, …}: drafts only. */
export const useUpdateConsentTemplate = () =>
  useTemplateMutation<{ id: string; body: UpdateConsentTemplateInput }>(
    async ({ id, body }) =>
      (await api.patch<ConsentTemplate>(`/consent-templates/${id}`, body)).data,
  );

/**
 * POST /consent-templates/{id}/approve {version} (doctor) or …/retire
 * {version} (retire a revision or discard a draft).
 */
export const useConsentTemplateAction = (action: 'approve' | 'retire') =>
  useTemplateMutation<{ id: string; version: number }>(
    async ({ id, version }) =>
      (
        await api.post<ConsentTemplate>(`/consent-templates/${id}/${action}`, {
          version,
        })
      ).data,
  );
